import React, {useState, useMemo, useCallback} from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Modal,
  Form,
  ListGroup,
  Badge,
  InputGroup,
  Pagination,
  Tabs,
  Tab,
  Table,
  Alert,
  Dropdown,
  ButtonGroup,
} from "react-bootstrap";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaUsers,
  FaEye,
  FaSearch,
  FaFileExcel,
  FaTruck,
  FaQrcode,
  FaUpload,
  FaUserPlus,
  FaCog,
  FaUserFriends,
} from "react-icons/fa";
import allCampaigns from "../data/campaigns.json";
import * as XLSX from "xlsx";
import QRInviteGenerator from "../components/QRInviteGeneratorNew";
import BulkImportModal from "../components/BulkImportModal";
import {HelpTooltip, HelpPopover} from "../components/HelpTooltip";

const KpiCard = ({icon, title, value, color}) => (
  <Card
    className={`shadow-sm rounded-4 border-0 border-start border-5 border-${color} h-100`}
  >
    <Card.Body className="d-flex align-items-center">
      <div className={`fs-2 text-${color} me-3`}>{icon}</div>
      <div>
        <div className="fs-4 fw-bold">{value}</div>
        <div className="text-muted text-uppercase small">{title}</div>
      </div>
    </Card.Body>
  </Card>
);

const Lists = ({lists, setLists, contacts, setContacts}) => {
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [editingList, setEditingList] = useState(null);
  const [listSearchTerm, setListSearchTerm] = useState(""); // Para la tabla principal
  const [memberSearchTerm, setMemberSearchTerm] = useState(""); // Para el modal
  const [selectedMembers, setSelectedMembers] = useState(new Set());

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingList(null);
    setListSearchTerm("");
    setMemberSearchTerm("");
  };

  const handleShowCreateModal = () => {
    setEditingList(null);
    setSelectedMembers(new Set());
    setShowModal(true);
  };

  const handleShowEditModal = (list) => {
    setEditingList(list);
    setSelectedMembers(new Set(list.members));
    setShowModal(true);
  };

  const handleShowDetailModal = (list) => {
    setSelectedList(list);
    setShowDetailModal(true);
  };

  const handleSaveList = (e) => {
    e.preventDefault();
    const form = e.target;

    const newList = {
      id: editingList ? editingList.id : Date.now(),
      name: form.listName.value,
      description: form.listDescription.value,
      members: Array.from(selectedMembers),
    };

    if (editingList) {
      // Actualizar
      setLists(lists.map((l) => (l.id === editingList.id ? newList : l)));
    } else {
      // Crear - Agregar al principio de la lista
      setLists([newList, ...lists]);
    }

    handleCloseModal();
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedList(null);
  };

  // Filtra los contactos en el modal de edición/creación
  const filteredContactsForSelection = contacts.filter((contact) =>
    `${contact.firstName} ${contact.lastName} ${contact.transportista}`
      .toLowerCase()
      .includes(memberSearchTerm.toLowerCase())
  );

  // Obtiene los detalles completos de los miembros de una lista
  const getMemberDetails = useCallback(
    (memberIds) => {
      return contacts.filter((contact) => memberIds.includes(contact.id));
    },
    [contacts]
  );

  // Obtiene las campañas enviadas a una lista específica
  const getCampaignsForList = (listId) => {
    return allCampaigns.filter((campaign) =>
      campaign.sentToListIds.includes(listId)
    );
  };

  const handleMemberSelectionChange = (contactId, isChecked) => {
    setSelectedMembers((prev) => {
      const newSet = new Set(prev);
      if (isChecked) {
        newSet.add(contactId);
      } else {
        newSet.delete(contactId);
      }
      return newSet;
    });
  };

  const handleSelectAllFiltered = () => {
    const filteredIds = filteredContactsForSelection.map((c) => c.id);
    setSelectedMembers((prev) => new Set([...prev, ...filteredIds]));
  };

  // Nuevas funciones para QR e Importación
  const handleShowQRModal = (list) => {
    setSelectedList(list);
    setShowQRModal(true);
  };

  const handleImportContacts = (importedContacts) => {
    // Agregar los contactos importados al estado global
    setContacts((prevContacts) => {
      const existingEmails = new Set(prevContacts.map((c) => c.email));
      const existingPhones = new Set(prevContacts.map((c) => c.phone));

      const newContacts = importedContacts.filter(
        (contact) =>
          !existingEmails.has(contact.email) &&
          !existingPhones.has(contact.phone)
      );

      const updatedContacts = importedContacts.filter(
        (contact) =>
          existingEmails.has(contact.email) || existingPhones.has(contact.phone)
      );

      // Actualizar contactos existentes
      const finalContacts = prevContacts.map((existing) => {
        const update = updatedContacts.find(
          (u) => u.email === existing.email || u.phone === existing.phone
        );
        return update ? {...existing, ...update} : existing;
      });

      return [...finalContacts, ...newContacts];
    });
  };

  // --- Cálculo de Métricas ---
  const listMetrics = useMemo(() => {
    const totalMembers = new Set();
    const campaignsPerList = {};

    lists.forEach((list) => {
      list.members.forEach((memberId) => totalMembers.add(memberId));
      campaignsPerList[list.id] = getCampaignsForList(list.id).length;
    });

    return {
      totalUniqueMembers: totalMembers.size,
      campaignsPerList,
    };
  }, [lists]);

  const handleExportAllLists = () => {
    const dataToExport = lists.map((list) => ({
      "Nombre de la Lista": list.name,
      Descripción: list.description,
      "Cantidad de Miembros": list.members.length,
      "Campañas Relacionadas": listMetrics.campaignsPerList[list.id] || 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Listas");
    XLSX.writeFile(workbook, "Resumen_Listas.xlsx");
  };

  const handleExportListMembers = (list) => {
    const members = getMemberDetails(list.members);
    const dataToExport = members.map((member) => ({
      Nombre: member.firstName,
      Apellido: member.lastName,
      Transportista: member.transportista,
      "Correo Electrónico": member.email,
      Teléfono: member.phone,
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Miembros");
    XLSX.writeFile(workbook, `Miembros_${list.name}.xlsx`);
  };

  const totalTransportistasInLists = useMemo(() => {
    const transportistas = new Set();
    lists.forEach((list) => {
      const members = getMemberDetails(list.members);
      members.forEach((member) => transportistas.add(member.transportista));
    });
    return transportistas.size;
  }, [lists, getMemberDetails]);

  // --- Lógica de Búsqueda y Paginación ---
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const filteredLists = useMemo(() => {
    return lists.filter(
      (list) =>
        list.name.toLowerCase().includes(listSearchTerm.toLowerCase()) ||
        list.description.toLowerCase().includes(listSearchTerm.toLowerCase())
    );
  }, [lists, listSearchTerm]);

  // Efecto para reiniciar la paginación al buscar
  React.useEffect(() => {
    setCurrentPage(1);
  }, [listSearchTerm]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredLists.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filteredLists.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderPaginationItems = () => {
    const items = [];
    if (totalPages <= 1) return null;

    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => paginate(currentPage - 1)}
        disabled={currentPage === 1}
      />
    );
    for (let number = 1; number <= totalPages; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => paginate(number)}
        >
          {number}
        </Pagination.Item>
      );
    }
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => paginate(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
    );
    return items;
  };

  return (
    <Container fluid className="p-4 bg-light">
      {/* Encabezado de Audiencias */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h2 className="fw-bold mb-1">
                <FaUserFriends className="me-3 text-primary" />
                Gestión de Audiencias
                <HelpPopover
                  title="¿Qué son las Audiencias?"
                  content={
                    <div>
                      <p className="mb-2">
                        Las audiencias son grupos organizados de contactos para
                        enviar comunicaciones dirigidas.
                      </p>
                      <p className="mb-2">
                        <strong>Puedes crear audiencias de 3 formas:</strong>
                      </p>
                      <ul className="mb-2">
                        <li>
                          <strong>Manual:</strong> Seleccionando contactos uno
                          por uno
                        </li>
                        <li>
                          <strong>Importación:</strong> Subiendo un archivo
                          Excel con múltiples contactos
                        </li>
                        <li>
                          <strong>Invitación QR:</strong> Generando un código QR
                          para que se suscriban automáticamente
                        </li>
                      </ul>
                      <p className="mb-0">
                        <strong>Tip:</strong> Una persona puede estar en
                        múltiples audiencias.
                      </p>
                    </div>
                  }
                />
              </h2>
              <p className="text-muted mb-0">
                Organiza y segmenta tus contactos para comunicaciones dirigidas
              </p>
            </div>
            <Dropdown as={ButtonGroup}>
              <Button
                variant="primary"
                size="lg"
                onClick={handleShowCreateModal}
                className="rounded-start-pill px-4"
              >
                <FaPlus className="me-2" />
                Nueva Audiencia
              </Button>
              <Dropdown.Toggle
                split
                variant="primary"
                size="lg"
                className="rounded-end-pill"
              />
              <Dropdown.Menu>
                <Dropdown.Item onClick={handleShowCreateModal}>
                  <FaUserPlus className="me-2" />
                  Crear Manualmente
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setShowImportModal(true)}>
                  <FaUpload className="me-2" />
                  Importar desde Excel
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item disabled className="text-muted small">
                  💡 Tip: También puedes generar códigos QR desde cada audiencia
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Col>
      </Row>

      {/* Sección de Ayuda Contextual */}
      {lists.length === 0 && (
        <Row className="mb-4">
          <Col>
            <Alert variant="info" className="border-0 shadow-sm">
              <div className="d-flex align-items-start">
                <FaUsers className="text-info me-3 mt-1" size={24} />
                <div>
                  <h5 className="fw-bold mb-2">
                    ¡Bienvenido a la Gestión de Audiencias!
                  </h5>
                  <p className="mb-3">
                    Parece que aún no tienes audiencias creadas. Las audiencias
                    te permiten organizar tus contactos en grupos para enviar
                    comunicaciones más efectivas y dirigidas.
                  </p>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleShowCreateModal}
                    >
                      <FaUserPlus className="me-1" />
                      Crear mi primera audiencia
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowImportModal(true)}
                    >
                      <FaUpload className="me-1" />
                      Importar contactos
                    </Button>
                  </div>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>
      )}

      {/* Fila de KPIs */}
      <Row className="mb-4">
        <Col md={4}>
          <KpiCard
            icon={<FaUsers />}
            title="Audiencias Activas"
            value={lists.length}
            color="primary"
          />
        </Col>
        <Col md={4}>
          <KpiCard
            icon={<FaUsers />}
            title="Contactos Únicos"
            value={listMetrics.totalUniqueMembers}
            color="info"
          />
        </Col>
        <Col md={4}>
          <KpiCard
            icon={<FaTruck />}
            title="Organizaciones"
            value={totalTransportistasInLists}
            color="success"
          />
        </Col>
      </Row>

      <Card className="shadow-sm rounded-4 border-0">
        <Card.Header className="bg-white p-3 border-bottom-0 d-flex justify-content-between align-items-center">
          <h5 className="mb-0 fw-bold">
            <FaUsers className="me-2 text-primary" />
            Todas las Audiencias
          </h5>
          <div className="d-flex align-items-center">
            <Button
              variant="outline-success"
              size="sm"
              onClick={handleExportAllLists}
              className="me-2"
            >
              <FaFileExcel className="me-2" />
              Exportar Resumen
            </Button>
            <InputGroup style={{width: "300px"}}>
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                placeholder="Buscar por nombre o descripción..."
                value={listSearchTerm}
                onChange={(e) => setListSearchTerm(e.target.value)}
              />
            </InputGroup>
          </div>
        </Card.Header>
        <Card.Body style={{overflow: "visible"}}>
          <div style={{minHeight: "400px"}}>
            <Table hover responsive className="align-middle">
              <thead>
                <tr>
                  <th>Nombre de la Lista</th>
                  <th>Descripción</th>
                  <th className="text-center">Miembros</th>
                  <th className="text-center">Campañas Relacionadas</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((list) => {
                  const campaignCount =
                    listMetrics.campaignsPerList[list.id] || 0;
                  return (
                    <tr key={list.id}>
                      <td className="fw-bold">{list.name}</td>
                      <td className="text-muted">{list.description}</td>
                      <td className="text-center">
                        <Badge bg="secondary" pill>
                          {list.members.length}
                        </Badge>
                      </td>
                      <td className="text-center">{campaignCount}</td>
                      <td className="text-center">
                        <Dropdown drop="auto" align="end">
                          <Dropdown.Toggle
                            variant="outline-secondary"
                            size="sm"
                          >
                            <FaCog />
                          </Dropdown.Toggle>
                          <Dropdown.Menu
                            popperConfig={{
                              placement: "bottom-end",
                              modifiers: [
                                {
                                  name: "flip",
                                  options: {
                                    fallbackPlacements: [
                                      "top-end",
                                      "bottom-start",
                                      "top-start",
                                    ],
                                  },
                                },
                                {
                                  name: "preventOverflow",
                                  options: {
                                    boundary: "clippingParents",
                                  },
                                },
                              ],
                            }}
                          >
                            <Dropdown.Item
                              onClick={() => handleShowDetailModal(list)}
                            >
                              <FaEye className="me-2" />
                              Ver Detalles
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => handleShowEditModal(list)}
                            >
                              <FaEdit className="me-2" />
                              Editar Audiencia
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item
                              onClick={() => handleShowQRModal(list)}
                            >
                              <FaQrcode className="me-2" />
                              Generar Código QR
                              <HelpTooltip text="Permite que las personas se unan automáticamente escaneando un código" />
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => handleExportListMembers(list)}
                              disabled={list.members.length === 0}
                            >
                              <FaFileExcel className="me-2" />
                              Exportar Miembros
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item
                              className="text-danger"
                              disabled={campaignCount > 0}
                              title={
                                campaignCount > 0
                                  ? "No se puede eliminar, tiene campañas asociadas"
                                  : "Eliminar audiencia"
                              }
                            >
                              <FaTrash className="me-2" />
                              Eliminar
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          {totalPages > 1 && (
            <div className="d-flex justify-content-center">
              <Pagination>{renderPaginationItems()}</Pagination>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* --- Modal para Crear/Editar Lista --- */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Form onSubmit={handleSaveList}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingList ? "Editar Lista" : "Crear Nueva Lista"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="listName">
              <Form.Label>Nombre de la Lista</Form.Label>
              <Form.Control
                type="text"
                name="listName"
                placeholder="Ej: Transportistas Zona Norte"
                defaultValue={editingList?.name}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="listDescription">
              <Form.Label>Descripción (Opcional)</Form.Label>
              <Form.Control
                as="textarea"
                name="listDescription"
                rows={2}
                placeholder="Una breve descripción de la lista"
                defaultValue={editingList?.description}
              />
            </Form.Group>
            <Form.Group className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Form.Label className="fw-bold mb-0">
                  Añadir Miembros
                </Form.Label>
                <div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="me-2"
                    onClick={handleSelectAllFiltered}
                  >
                    Seleccionar Visibles
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setSelectedMembers(new Set())}
                  >
                    Limpiar Selección
                  </Button>
                </div>
              </div>
              <InputGroup className="mb-3">
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Buscar por nombre o transportista..."
                  value={memberSearchTerm}
                  onChange={(e) => setMemberSearchTerm(e.target.value)}
                />
              </InputGroup>
              <ListGroup style={{maxHeight: "300px", overflowY: "auto"}}>
                {filteredContactsForSelection.map((contact) => (
                  <ListGroup.Item key={contact.id}>
                    <Form.Check
                      type="checkbox"
                      id={`checkbox-contact-${contact.id}`}
                      checked={selectedMembers.has(contact.id)}
                      onChange={(e) =>
                        handleMemberSelectionChange(
                          contact.id,
                          e.target.checked
                        )
                      }
                      label={
                        <div>
                          <strong>{`${contact.firstName} ${contact.lastName}`}</strong>
                          <div className="text-muted small">
                            {contact.transportista}
                          </div>
                        </div>
                      }
                    />
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cerrar
            </Button>
            <Button type="submit" variant="primary">
              {editingList ? "Actualizar Lista" : "Guardar Lista"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* --- Modal para Ver Detalles de la Lista --- */}
      {selectedList && (
        <Modal
          show={showDetailModal}
          onHide={handleCloseDetailModal}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <div className="d-flex justify-content-between align-items-center w-100">
              <Modal.Title>Detalles de: {selectedList.name}</Modal.Title>
              <Button
                variant="success"
                size="sm"
                onClick={() => handleExportListMembers(selectedList)}
              >
                <FaFileExcel className="me-2" />
                Exportar Miembros
              </Button>
            </div>
          </Modal.Header>
          <Modal.Body>
            <Tabs
              defaultActiveKey="members"
              id="list-details-tabs"
              className="mb-3"
            >
              <Tab
                eventKey="members"
                title={`Miembros (${selectedList.members.length})`}
              >
                <ListGroup variant="flush">
                  {getMemberDetails(selectedList.members).map((member) => (
                    <ListGroup.Item
                      key={member.id}
                      className="d-flex justify-content-between align-items-start"
                    >
                      <div>
                        <div className="fw-bold">
                          {member.firstName} {member.lastName}
                        </div>
                        <span className="text-muted">{member.email}</span>
                      </div>
                      <Badge bg="info" pill>
                        {member.phone}
                      </Badge>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Tab>
              <Tab eventKey="campaigns" title="Campañas Enviadas">
                <p className="text-muted mt-3">
                  Aquí se mostrará un historial de todas las campañas de
                  comunicación que se han enviado a esta lista.
                </p>
                <ListGroup>
                  {getCampaignsForList(selectedList.id).map((campaign) => (
                    <ListGroup.Item key={campaign.id}>
                      {campaign.name}
                      <br />
                      <small className="text-muted">
                        Enviada:{" "}
                        {new Date(campaign.sentDate).toLocaleDateString(
                          "es-CL"
                        )}
                      </small>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Tab>
            </Tabs>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseDetailModal}>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Modal para Generar QR */}
      {selectedList && (
        <QRInviteGenerator
          show={showQRModal}
          onHide={() => setShowQRModal(false)}
          audienceName={selectedList.name}
          audienceId={selectedList.id}
        />
      )}

      {/* Modal para Importación Masiva */}
      <BulkImportModal
        show={showImportModal}
        onHide={() => setShowImportModal(false)}
        onImport={handleImportContacts}
        organizations={[]} // Pasaríamos las organizaciones aquí
      />
    </Container>
  );
};

export default Lists;
